<script>
/**
 * G-Tooltip — tooltip **único**, controlado por métodos, para grades/listas.
 *
 * Em vez de criar um `v-tooltip` por célula (caro: cada hover re-renderiza a
 * grade inteira), a tela mantém **uma** instância e chama `show()`/`hide()` a
 * partir dos próprios eventos (`mouseenter`/`mouseleave`, foco, clique etc.).
 *
 * O conteúdo é **texto simples** — `show(el, "texto")` — ou markup próprio da tela
 * pelo **slot default (scoped)**, que recebe `{ text, opened }`. O componente não
 * interpreta o conteúdo (listas, campos, ícones etc. ficam na tela).
 *
 * @example
 * <g-tooltip ref="tooltip" placement="bottom" :offset="8" />
 *
 * this.$refs.tooltip.show(event.currentTarget, "Linha 1\nMotivo: ...");
 * this.$refs.tooltip.hide();
 */
export default {
  name: "GTooltip",
  props: {
    /** Distância (px) entre o ponto/elemento de referência e o tooltip. */
    offset: {
      type: Number,
      default: 0
    },
    /** Largura máxima do conteúdo (px). */
    maxWidth: {
      type: [Number, String],
      default: 320
    },
    /** Posição preferida em relação ao ponto informado. */
    placement: {
      type: String,
      default: "top",
      validator: value => ["top", "bottom", "left", "right"].includes(value)
    },
    /**
     * Variante visual (paleta):
     * - `dark` (padrão): fundo preto com pouca transparência;
     * - `error` / `warning` / `success` / `info`: cores da paleta do app;
     * - `light`: fundo branco com texto escuro.
     */
    variant: {
      type: String,
      default: "dark",
      validator: value =>
        ["dark", "error", "warning", "success", "info", "light"].includes(value)
    },
    /** Cor de fundo — sobrescreve a paleta da variante. */
    backgroundColor: {
      type: String,
      default: ""
    },
    /** Cor do texto — sobrescreve a paleta da variante. */
    textColor: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      opened: false,
      activator: null,
      // Texto padrão do slot; a tela pode substituí-lo por markup próprio.
      text: ""
    };
  },
  computed: {
    /** Classe do conteúdo (inclui a variante, gancho para testes). */
    contentClass() {
      return `g-tooltip__content g-tooltip__content--${this.variant}`;
    },
    /** Overrides de cor vindos de props. */
    bodyStyle() {
      const style = {};

      if (this.backgroundColor) {
        style.backgroundColor = this.backgroundColor;
      }

      if (this.textColor) {
        style.color = this.textColor;
      }

      return style;
    }
  },
  methods: {
    /**
     * Abre — ou **reancora** — o tooltip no elemento informado (padrão da Apuração
     * CAPEX: âncora via `:activator`).
     *
     * O componente é **genérico**: recebe apenas um texto — ou nada, quando a tela
     * preenche o slot com markup próprio — e não interpreta o conteúdo.
     *
     * **Não** fechamos antes de reabrir: a transição de saída do Vuetify engolia a
     * reabertura no tick seguinte e o tooltip ficava alternando abrir/fechar a cada
     * célula/linha. O reposicionamento é garantido pelo `activate()` forçado abaixo.
     *
     * @param {HTMLElement} element Elemento de referência (célula, linha, botão…)
     * @param {string} [text] Texto padrão (usado quando não há conteúdo no slot)
     * @returns {this}
     */
    show(element, text = "") {
      if (!element) {
        return this;
      }

      this.text = text;
      this.activator = element;
      this.opened = true;

      // O Vuetify copia as dimensões do ativador em `activate()` (que chama
      // `updateDimensions()`). Trocar o `activator` com o tooltip aberto **não**
      // reposiciona sozinho (a posição vem de `dimensions`, não de um `computed`),
      // então forçamos o recálculo sem fechar/reabrir — fechar era o que causava o
      // efeito de piscar/alternar.
      this.$nextTick(() => {
        const tooltip = this.$refs.tooltip;

        if (tooltip && typeof tooltip.activate === "function") {
          tooltip.activate();
        }
      });

      return this;
    },
    /** Fecha o tooltip. @returns {this} */
    hide() {
      this.opened = false;

      return this;
    },
    /**
     * Alterna aberto/fechado (útil para `click`/`focus`).
     * @returns {this}
     */
    toggle(element, text) {
      return this.opened ? this.hide() : this.show(element, text);
    }
  }
};
</script>

<template>
  <v-tooltip
    ref="tooltip"
    v-model="opened"
    :activator="activator"
    :bottom="placement === 'bottom'"
    :left="placement === 'left'"
    :max-width="maxWidth"
    :nudge-bottom="placement === 'bottom' ? offset : 0"
    :nudge-left="placement === 'left' ? offset : 0"
    :nudge-right="placement === 'right' ? offset : 0"
    :nudge-top="placement === 'top' ? offset : 0"
    :open-on-click="false"
    :open-on-focus="false"
    :open-on-hover="false"
    :right="placement === 'right'"
    :top="placement === 'top'"
    :content-class="contentClass"
  >
    <div
      class="g-tooltip__body"
      :class="`g-tooltip__body--${variant}`"
      :style="bodyStyle"
    >
      <slot :text="text" :opened="opened">
        <span class="g-tooltip__text">{{ text }}</span>
      </slot>
    </div>
  </v-tooltip>
</template>

<style lang="scss" scoped>
.g-tooltip__body {
  // `dark` (padrão): preto com pouca transparência.
  background-color: rgba(33, 33, 33, 0.97);
  border-radius: 4px;
  color: #ffffff;
  font-size: 12px;
  line-height: 1.4;
  overflow-wrap: break-word;
  padding: 8px 14px;
  white-space: normal;
}

.g-tooltip__body--error {
  background-color: #c62828;
}

.g-tooltip__body--warning {
  background-color: #f9a825;
  color: #212121;
}

.g-tooltip__body--success {
  background-color: #2e7d32;
}

.g-tooltip__body--info {
  background-color: #0277bd;
}

.g-tooltip__body--light {
  background-color: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.12);
  color: #212121;
}

.g-tooltip__text {
  white-space: pre-line;
}
</style>

<style lang="scss">
/**
 * Zera fundo/padding padrão do Vuetify no conteúdo do tooltip: a pintura fica no
 * `.g-tooltip__body` (elemento nosso), o que permite variar a cor por props sem
 * `!important` e sem CSS global por variante. O seletor fica isolado pela classe
 * aplicada via `content-class`.
 */
.g-tooltip__content.v-tooltip__content {
  background: transparent !important;
  color: inherit;
  opacity: 1 !important;
  padding: 0 !important;
}
</style>

